import {
  APP_CONFIGURATION_URL,
  APP_VERSION_URL,
  HEALTH_CHECK_API_URL,
  SELF_SERVICE_THEMES_URL,
  SELF_SERVICE_WORKFLOW
} from 'constants/ApiConstants';

import * as types from './actionTypes';

export const forcePageReload = () => ({
  type: types.FORCE_PAGE_RELOAD,
});

export const forcePageReloadSuccess = () => ({
  type: types.FORCE_PAGE_RELOAD_SUCCESS,
});

/**
 * @param {undefined} payload
 * @param {Object} [meta={}]
 * @return {import('modules/global/generic.types').Action<undefined>}
 */
export const applicationLoaded = (payload, meta = {}) => ({
  type: types.APPLICATION_LOADED,
  payload,
  meta,
});

/**
 * @param {undefined} payload
 * @param {Object} [meta={}]
 * @return {import('modules/global/generic.types').ErrorAction}
 */
export const applicationLoadedFailure = (payload, meta = {}) => ({
  type: types.APPLICATION_LOADED_FAILED,
  payload,
  error: true,
  meta,
});

/**
 * @param {undefined} payload
 * @param {Object} [meta={}]
 * @return {import('modules/global/generic.types').Action<undefined>}
 */
export const isApplicationReady = (payload, meta = {}) => ({
  type: types.APPLICATION_IS_READY,
  payload,
  meta,
});

/**
 * @param {undefined} payload
 * @param {Object} [meta={}]
 * @returns {import('./types').ConnectionLostAction}
 */
export const connectionLost = (payload, meta = {}) => ({
  type: types.CONNECTION_LOST,
  payload,
  meta,
});

/**
 * @param {undefined} payload
 * @param {Object} [meta={}]
 * @returns {import('./types').ConnectionEstablishedAction}
 */
export const connectionEstablished = (payload, meta = {}) => ({
  type: types.CONNECTION_ESTABLISHED,
  payload,
  meta,
});

/** @returns {import("./types").FetchAppConfigurationAction} */
export const fetchAppConfiguration = () => ({
  type: types.FETCH_APP_CONFIGURATION,
  payload: {
    request: {
      method: 'GET',
      url: `${APP_CONFIGURATION_URL}`,
    },
  },
});

/**
 * @param {import("./reducer/config").AppConfigReducer} configuration
 * @returns {import('./types').FetchAppConfigurationSuccessAction}
 */
export const fetchAppConfigurationSuccess = configuration => ({
  type: types.FETCH_APP_CONFIGURATION_SUCCESS,
  payload: configuration,
});

/**
 * @param {string} message
 * @param {Object} [meta={}]
 * @returns {import('./types').FetchAppConfigurationFailureAction}
 */
export const fetchAppConfigurationFailure = (message, meta = {}) => ({
  type: types.FETCH_APP_CONFIGURATION_FAILURE,
  payload: message,
  error: true,
  meta,
});

/**
 * @param {boolean} isCompatible
 * @param {Object?} meta
 * @returns {import('modules/global/generic.types').Action<boolean>}
 */
export const setBrowserIsCompatible = (isCompatible, meta = {}) => ({
  type: types.SET_BROWSER_NOTIFICATION_COMPATIBILITY,
  payload: isCompatible,
  meta,
});

/**
 * @param {{ url: string, filename: string, options: Object }} params
 * @param {Object} [meta={}]
 * @returns {import('./types').GenerateQRCodeAction}
 */
export const generateQRCode = ({ url, filename, options }, meta = {}) => ({
  type: types.GENERATE_QR_CODE,
  payload: { url, filename, options },
  meta,
});

/**
 * @param {Object} [meta={}]
 * @returns {import('modules/global/generic.types').PayloadAction<import('modules/global/generic.types').GetRequest<{ t: number }>>}
 */
export const checkVersion = (meta = {}) => ({
  type: types.CHECK_VERSION_REQUEST,
  payload: {
    request: {
      method: 'GET',
      url: `${APP_VERSION_URL}`,
      params: { t: Date.now() },
      baseURL: null, // We don't query the API for this
    },
  },
  meta,
});

/**
 * @param {string} selector
 * @returns {import('./types').ScrollIntoViewAction}
 */
export const scrollIntoView = (selector, meta = {}) => ({
  type: types.SCROLL_INTO_VIEW,
  payload: selector,
  meta,
});

/**
 * @param {Object} [meta={}]
 * @returns {import("./types").OpenConcurrencyModalAction}
 */
export const openConcurrencyModal = (meta = {}) => ({
  type: types.OPEN_CONCURRENCY_MODAL,
  meta,
});

/**
 * @param {Object} [meta={}]
 * @returns {import("./types").CloseConcurrencyModalAction}
 */
export const closeConcurrencyModal = (meta = {}) => ({
  type: types.CLOSE_CONCURRENCY_MODAL,
  meta,
});

/**
 * @param {Object} [meta={}]
 * @returns {import("./types").ConcurrencyRefreshPageAction}
 */
export const concurrencyRefreshPage = (meta = {}) => ({
  type: types.CONCURRENCY_REFRESH_PAGE,
  meta,
});

/**
 * @template T
 * @param {import("modules/global/generic.types").Request} request
 * @param {T} payload
 * @param {Object} [meta={}]
 * @returns {import("modules/global/generic.types").ActionRequest<T>}
 */
export const anonymousRequest = (request, payload = {}, meta = {}) => ({
  type: types.ANONYMOUS_REQUEST,
  payload: {
    request,
    ...payload,
  },
  meta: { '@app-request': true, ...meta },
});

export const getFuelTypes = (meta = {}) => ({
  type: types.GET_FUEL_TYPES,
  payload: {
    request: {
      method: 'GET',
      url: 'refdata/fuelTypes',
    },
  },
  meta: { '@app-request': true, ...meta },
});

/** @type {import('./types').FetchHealthCheckAction} */
export const fetchHealthCheck = (meta = {}) => ({
  type: types.FETCH_HEALTH_CHECK,
  payload: {
    request: {
      method: 'GET',
      url: HEALTH_CHECK_API_URL,
    },
  },
  meta: {
    ...meta,
    '@app-request': true,
    '@no-authentication': true,
  },
});

/** @type {import('./types').FetchHealthCheckSuccessAction} */
export const fetchHealthCheckSuccess = (payload, meta = {}) => ({
  type: types.FETCH_HEALTH_CHECK_SUCCESS,
  payload,
  meta,
});

/** @type {import('./types').FetchHealthCheckFailureAction} */
export const fetchHealthCheckFailure = (payload, meta = {}) => ({
  type: types.FETCH_HEALTH_CHECK_FAILURE,
  payload,
  error: true,
  meta,
});

export const fetchAPPThemes = (meta = {}) => ({
  type: types.FETCH_THEMES,
  payload: {
    request: {
      method: 'GET',
      url: SELF_SERVICE_THEMES_URL,
    },
  },
  meta: {
    '@app-request': true,
    ...meta,
  },
});

export const blockLocationChange = (payload, meta = {}) => ({
  type: types.BLOCK_LOCATION_CHANGE,
  payload,
  meta,
});

export const unblockLocationChange = (payload, meta = {}) => ({
  type: types.UNBLOCK_LOCATION_CHANGE,
  payload,
  meta,
});

export const fetchSelfServiceWorkflow = (meta = {}) => ({
  type: types.FETCH_SELF_SERVICE_WORKFLOW,
  payload: {
    request: {
      method: 'GET',
      url: SELF_SERVICE_WORKFLOW,
    },
  },
  meta: {
    '@app-request': true,
    ...meta,
  },
});
